import { toast } from 'sonner';
import { create } from 'zustand';
import { INVENTORY_MANAGE_TYPE, PRICING_TYPES } from '../utility/Constant';

// Validation helper functions
const validateService = (service) => {
    if (!service) {
        toast.error('Service name is required');
        return false;
    }
    return true;
};

const validateCategory = (category) => {
    if (!category) {
        toast.error('Service category is required');
        return false;
    }
    return true;
};

const validateVehicleMakes = (makes) => {
    if (!makes || makes.length === 0) {
        toast.error('Vehicle make is required');
        return false;
    }
    return true;
};

const validateShopServiceReference = (reference) => {
    if (!reference || reference.trim() === '') {
        toast.error('Shop reference is required');
        return false;
    }
    return true;
};

const validateLabor = (labor) => {
    if (labor && (labor.qty === null || labor.qty === undefined || labor.cost === null || labor.cost === undefined)) {
        toast.error('Labor quantity and cost are required');
        return false;
    }
    return true;
};

const initialState = {
    showLoader: false,
    savedServiceParams: {
        pagination: true,
        page: 1,
        limit: 10,
        serviceCategory: [],
        vehicleMake: [],
        search: '',
        sortOrder: 'asc',
        sortBy: 'serviceName',
    },

    // Modal State
    selectedSavedServiceId: null,
    isModalOpen: false,

    // Form Data for Add/Edit Modal
    formData: {
        isLoading: false,
        selectedService: null,
        shopServiceReference: '',
        selectedCategory: null,
        selectedMakes: [],
        description: '',
        fixedAmount: '0',
        pricingType: PRICING_TYPES.FXIED,
        labor: null,
        partsData: [],
    },

    // Dropdown Data
    allServices: [],
    serviceCategories: [],
    vehicleMakes: [],
};

const savedServiceStore = (set, get) => ({
    ...initialState,

    setShowLoader: (data) => {
        set({ showLoader: data });
    },
    setSavedServiceParams: (data) => {
        set({ savedServiceParams: data });
    },

    openModal: (serviceId = null) => {
        set({
            isModalOpen: true,
            selectedSavedServiceId: serviceId,
        });
    },

    closeModal: () => {
        set({
            isModalOpen: false,
            selectedSavedServiceId: null,
        });
    },
    // ---------- Optimized FormData Setters ----------
    setFormIsLoading: (isLoading) =>
        set((state) => {
            if (state.formData.isLoading === isLoading) return state;
            return {
                formData: { ...state.formData, isLoading },
            };
        }),

    setFormSelectedService: (selectedService) =>
        set((state) => {
            if (state.formData.selectedService === selectedService) return state;
            return {
                formData: { ...state.formData, selectedService },
            };
        }),

    setFormShopServiceReference: (shopServiceReference) =>
        set((state) => {
            if (state.formData.shopServiceReference === shopServiceReference) return state;
            return {
                formData: { ...state.formData, shopServiceReference },
            };
        }),

    setFormSelectedCategory: (selectedCategory) =>
        set((state) => {
            if (state.formData.selectedCategory === selectedCategory) return state;
            return {
                formData: { ...state.formData, selectedCategory },
            };
        }),

    setFormSelectedMakes: (selectedMakes) =>
        set((state) => {
            if (state.formData.selectedMakes === selectedMakes) return state;
            return {
                formData: { ...state.formData, selectedMakes },
            };
        }),

    setFormDescription: (description) =>
        set((state) => {
            if (state.formData.description === description) return state;
            return {
                formData: { ...state.formData, description },
            };
        }),

    setFormFixedAmount: (fixedAmount) =>
        set((state) => {
            if (state.formData.fixedAmount === fixedAmount) return state;
            return {
                formData: { ...state.formData, fixedAmount },
            };
        }),

    setFormPricingType: (pricingType) =>
        set((state) => {
            if (state.formData.pricingType === pricingType) return state;
            return {
                formData: { ...state.formData, pricingType },
            };
        }),

    setFormPartsData: (partsData) =>
        set((state) => {
            if (state.formData.partsData === partsData) return state;
            return {
                formData: { ...state.formData, partsData },
            };
        }),

    clearPartsData: () =>
        set((state) => ({
            formData: { ...state.formData, partsData: [] },
        })),

    setFormPricingType: (pricingType) =>
        set((state) => {
            if (state.formData.pricingType === pricingType) return state;
            return {
                formData: { ...state.formData, pricingType },
            };
        }),

    setFormLabor: (labor) =>
        set((state) => {
            if (state.formData.labor === labor) return state;
            return {
                formData: { ...state.formData, pricingType: PRICING_TYPES.LABOUR, labor },
            };
        }),

    setFormData: (formData) =>
        set(() => ({
            formData,
        })),

    // Optimized validation and payload creation
    getSavePayload: () => {
        const { formData } = get();

        // Validate required fields
        if (!validateService(formData?.selectedService)) {
            return { isValid: false, payload: {} };
        }

        if (!validateCategory(formData?.selectedCategory)) {
            return { isValid: false, payload: {} };
        }

        if (!validateVehicleMakes(formData?.selectedMakes)) {
            return { isValid: false, payload: {} };
        }

        if (!validateShopServiceReference(formData?.shopServiceReference)) {
            return { isValid: false, payload: {} };
        }

        // Only validate labor if pricing type is LABOUR
        if (formData.pricingType === PRICING_TYPES.LABOUR && !validateLabor(formData?.labor)) {
            return { isValid: false, payload: {} };
        }
        // Validate fixed amount if pricing type is FIXED
        if (formData.pricingType === PRICING_TYPES.FXIED && (!formData.fixedAmount || Number(formData.fixedAmount) <= 0)) {
            toast.error('Fixed amount must be greater than 0');
            return { isValid: false, payload: {} };
        }

        // Build payload with proper structure
        const payload = {
            service: formData.selectedService.serviceSubCategory._id,
            serviceCategory: formData.selectedCategory._id,
            pricingType: formData.pricingType || PRICING_TYPES.FXIED,
            shopServiceReference: formData.shopServiceReference,
            isGroup: true, // Always include isGroup: true to support grouped makes
        };

        // Handle vehicle makes - if "all" is selected, send only ["all"]
        const hasAllSelected = formData.selectedMakes.some((make) => make._id === 'all');
        if (hasAllSelected) {
            payload.vehicleMake = ['all'];
        } else {
            payload.vehicleMake = formData.selectedMakes.map((make) => make._id);
        }
        // Handle optional description
        if (formData.description) {
            payload.description = formData.description;
        }

        // Handle different pricing types
        if (formData.pricingType === PRICING_TYPES.FXIED) {
            // For FIXED pricing: add fixedAmount and description (if present)
            if (formData.fixedAmount) {
                payload.fixedPrice = Number(formData.fixedAmount) || 0;
            }
        } else if (formData.pricingType === PRICING_TYPES.LABOUR) {
            // For LABOUR pricing: add labor and parts (same as before)

            // Add labor if present and valid
            if (formData.labor && formData.labor.qty !== null && formData.labor.cost !== null) {
                payload.labor = {
                    qty: Number(formData.labor.qty) || 0,
                    price: Number(formData.labor.cost) || 0,
                };
            }

            // Add parts if present
            if (formData.partsData && formData.partsData.length > 0) {
                payload.parts = formData.partsData
                    .filter((part) => part.type && (part.type === INVENTORY_MANAGE_TYPE.MANAGED || part.type === INVENTORY_MANAGE_TYPE.UNMANAGED))
                    .map((part) => {
                        if (part.type === INVENTORY_MANAGE_TYPE.UNMANAGED) {
                            return {
                                inventoryType: INVENTORY_MANAGE_TYPE.UNMANAGED,
                                partName: part.partName,
                                qty: Number(part.partQty) || 0,
                                price: Number(part.partCost) || 0,
                            };
                        }
                        if (part.type === INVENTORY_MANAGE_TYPE.MANAGED) {
                            return {
                                inventoryType: INVENTORY_MANAGE_TYPE.MANAGED,
                                inventory: part.part._id,
                                qty: Number(part.partQty) || 0,
                                price: Number(part.partCost) || 0,
                            };
                        }
                    });
            } else {
                payload.parts = [];
            }
        }

        return { isValid: true, payload };
    },

    // Helper function to validate form before submission
    validateForm: () => {
        const { formData } = get();

        // Base validation for all pricing types
        const baseValidation =
            validateService(formData?.selectedService) &&
            validateCategory(formData?.selectedCategory) &&
            validateVehicleMakes(formData?.selectedMakes) &&
            validateShopServiceReference(formData?.shopServiceReference);

        // Additional validation based on pricing type
        if (formData.pricingType === PRICING_TYPES.LABOUR) {
            return baseValidation && validateLabor(formData?.labor);
        } else if (formData.pricingType === PRICING_TYPES.FXIED) {
            // For FIXED pricing, validate fixed amount
            const isFixedAmountValid = formData.fixedAmount && Number(formData.fixedAmount) > 0;
            if (!isFixedAmountValid) {
                toast.error('Fixed amount must be greater than 0');
            }
            return baseValidation && isFixedAmountValid;
        }

        return baseValidation;
    },

    // Helper function to get form errors
    getFormErrors: () => {
        const { formData } = get();
        const errors = [];

        if (!formData?.selectedService) errors.push('Service name is required');
        if (!formData?.selectedCategory) errors.push('Service category is required');
        if (!formData?.selectedMakes?.length) errors.push('Vehicle make is required');
        if (!formData?.shopServiceReference || formData.shopServiceReference.trim() === '') errors.push('Shop reference is required');
        if (formData?.labor && (formData.labor.qty === null || formData.labor.cost === null)) {
            errors.push('Labor quantity and cost are required');
        }

        return errors;
    },

    // Reset all formData fields back to initial
    resetFormData: () =>
        set((state) => ({
            formData: { ...initialState.formData },
        })),

    // Helper function to update multiple form fields at once
    updateFormData: (updates) =>
        set((state) => ({
            formData: { ...state.formData, ...updates },
        })),

    // Helper function to add a part to the parts array
    addPart: (part) =>
        set((state) => ({
            formData: {
                ...state.formData,
                pricingType: PRICING_TYPES.LABOUR,
                partsData: [...state.formData.partsData, part],
            },
        })),

    // Helper function to remove a part from the parts array by index
    removePart: (index) =>
        set((state) => ({
            formData: {
                ...state.formData,
                partsData: state.formData.partsData.filter((_, i) => i !== index),
            },
        })),

    // Helper function to update a specific part by index
    updatePart: (index, updates) =>
        set((state) => ({
            formData: {
                ...state.formData,
                partsData: state.formData.partsData.map((part, i) => (i === index ? { ...part, ...updates } : part)),
            },
        })),

    // Helper function to calculate total amount
    calculateTotalAmount: () => {
        const { formData } = get();
        let total = 0;

        // Add labor amount
        if (formData.labor && formData.labor.qty && formData.labor.cost) {
            total += Number(formData.labor.qty) * Number(formData.labor.cost);
        }

        // Add parts amount
        if (formData.partsData && formData.partsData.length > 0) {
            total += formData.partsData.reduce((sum, part) => {
                return sum + (Number(part.partQty) || 0) * (Number(part.partCost) || 0);
            }, 0);
        }

        return total;
    },
});

const useSavedServiceStore = create(savedServiceStore);

export default useSavedServiceStore;
